import React from "react";

interface DisplayAmountProps {
  amount: number | string; // Accept both number and string
  currency?: string;
  decimal?: number; // Optional decimal places override
  fallbackText?: string;
  className?: string;
}

const currencySymbols: Record<string, string> = {
  // Traditional currencies
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  AUD: "$",
  CAD: "$",
  CNY: "¥",
  INR: "₹",
  KRW: "₩",
  CHF: "CHF",
  SEK: "kr",
  NOK: "kr",
  DKK: "kr",
  PLN: "zł",
  CZK: "Kč",
  HUF: "Ft",
  RUB: "₽",
  BRL: "R$",
  MXN: "$",
  ZAR: "R",
  SGD: "$",
  HKD: "HK$",
  NZD: "$",
  THB: "฿",
  MYR: "RM",
  PHP: "₱",
  IDR: "Rp",
  VND: "₫",
  TRY: "₺",
  ILS: "₪",
  EGP: "£",
  AED: "د.إ",
  SAR: "﷼",
};

const currencyFormatting: Record<string, { thousandsSep: string; decimalSep: string }> = {
  VND: { thousandsSep: ".", decimalSep: "," },
  IDR: { thousandsSep: ".", decimalSep: "," },
};

// Removed crypto-specific logic

const DisplayAmount: React.FC<DisplayAmountProps> = ({ 
  amount, 
  currency = "USD", 
  decimal, // Optional decimal override
  fallbackText = "Invalid amount",
  className = ""
}) => {
  const formatAmount = (value: number | string, curr: string, decimalPlaces?: number): string => {
    try {
      if (typeof curr !== 'string' || curr.trim() === '') {
        curr = 'USD';
      }

      const normalizedCurrency = curr.toUpperCase().trim();
      const symbol = currencySymbols[normalizedCurrency] || normalizedCurrency;
      const formatting = currencyFormatting[normalizedCurrency];

      let numericValue: number;
      let originalStringValue: string | null = null;

      // Handle input validation
      if (typeof value === "string") {
        originalStringValue = value.trim();
        numericValue = parseFloat(originalStringValue);
      } else {
        numericValue = value;
      }

      if (!isFinite(numericValue) || isNaN(numericValue)) {
        throw new Error('Invalid number');
      }

      // Handle very large numbers
      if (Math.abs(numericValue) >= Number.MAX_SAFE_INTEGER) {
        return `${symbol}${numericValue.toExponential(2)}`;
      }

      const isNegative = numericValue < 0;
      const absValue = Math.abs(numericValue);

      let formattedNumber: string;

      // If decimal prop is provided, use it. Otherwise preserve original precision for strings or use 2 decimals for numbers
      if (typeof decimalPlaces === 'number' && decimalPlaces >= 0) {
        formattedNumber = absValue.toFixed(decimalPlaces);
      } else if (originalStringValue !== null && typeof value === "string") {
        const cleanValue = originalStringValue.startsWith('-') ? originalStringValue.substring(1) : originalStringValue;
        formattedNumber = cleanValue;
      } else {
        formattedNumber = absValue.toFixed(2);
      }

      const [integerPart, decimalPart] = formattedNumber.split('.');
      const thousandsSep = formatting?.thousandsSep || ",";
      const decimalSep = formatting?.decimalSep || ".";

      // Add thousands separators (only for integer part)
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);

      // Construct final string
      let result = '';
      if (isNegative) result = '-';
      
      result += symbol + formattedInteger;
      if (decimalPart) {
        result += decimalSep + decimalPart;
      }

      return result;
    } catch (error) {
      console.warn('DisplayAmount formatting error:', error);
      return fallbackText;
    }
  };

  // Validate props
  if (amount === null || amount === undefined) {
    return <span className={className} title="Amount is null or undefined">{fallbackText}</span>;
  }

  const formattedAmount = formatAmount(amount, currency, decimal);
  
  return (
    <span 
      className={className}
      title={`${amount} ${currency.toUpperCase()}`}
      data-testid="display-amount"
      data-currency-type="currency"
    >
      {formattedAmount}
    </span>
  );
};

export default DisplayAmount;